import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Button, CircularProgress, FormControlLabel, MenuItem, Paper, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import { Checkbox } from '@mui/material';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import StoreLabelDialog from './StoreLabelDialog';   // Q3
import { StockService } from '../../services/stock.service';
import { StockLot, StockLotFilters, FabricGrade } from '../../interfaces/stock.interface';
import { Store } from '../../interfaces/store.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import { ItemService } from '../../services/item.service';
import { Item } from '../../interfaces/item.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { InventorySettingsService } from '../../services/inventorySettings.service';
import { triggerDownload } from '../statutoryRegisters/shared';
import ItemAutocomplete from '../merchandising/ItemAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { humanizeEnum, formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const GRADES: FabricGrade[] = ['A', 'B', 'C', 'REJECT'];
const dash = (value: string | number | null | undefined): string =>
    value === null || value === undefined || value === '' ? '—' : String(value);

const emptyFilters: StockLotFilters = {};
const PAGE_SIZE = 20;

/**
 * Fabric Lot Register (inventory-lots close-out, task 5). Server-paginated over
 * `StockController#searchLots`; table columns mirror `StockLotXlsxService.HEADERS` exactly.
 * "Export XLSX" always sends the last *applied* filter set (not the live, unapplied form state).
 */
const FabricLotRegister: React.FC = () => {
    const toast = useToast();
    const [searchParams] = useSearchParams();

    const [stores, setStores] = useState<Store[]>([]);
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    // ---- Q3: lot labels ----
    const canManageItems = usePermission(PERMISSIONS.ITEM_MANAGE);
    const canPrintLabels = usePermission(PERMISSIONS.STORE_LABEL_PRINT);
    const canLabel = canManageItems || canPrintLabels;
    const [labelIds, setLabelIds] = useState<string[]>([]);
    const [labelOpen, setLabelOpen] = useState(false);

    // Live form state (not yet applied).
    const [formStoreId, setFormStoreId] = useState<string>(searchParams.get('storeId') ?? '');
    const [formItemId, setFormItemId] = useState<string>(searchParams.get('itemId') ?? '');
    const [formShade, setFormShade] = useState<string>('');
    const [formDyeLot, setFormDyeLot] = useState<string>('');
    const [formGrade, setFormGrade] = useState<FabricGrade | ''>('');
    const [formOnlyAvailable, setFormOnlyAvailable] = useState<boolean>(false);
    const [formSearch, setFormSearch] = useState<string>('');

    // Applied filters — the set actually sent to the search/xlsx endpoints.
    const [appliedFilters, setAppliedFilters] = useState<StockLotFilters>({
        storeId: searchParams.get('storeId') ?? undefined,
        itemId: searchParams.get('itemId') ?? undefined,
    });

    const [rows, setRows] = useState<StockLot[]>([]);
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // G6b: under AVERAGE costing a lot's cost is its receipt cost only (relief charges the store average).
    const [averageCosting, setAverageCosting] = useState(false);
    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        InventorySettingsService.get(institutionId)
            .then(s => setAverageCosting(s?.lotCostingMethod === 'AVERAGE'))
            .catch(() => setAverageCosting(false));   // the label is informational; FIFO wording on failure
    }, []);

    useEffect(() => {
        getStoresByInstitutionId(0, 100, 'ALL')
            .then(res => setStores(res.content))
            .catch(err => toast.showApiError(err, 'Failed to load stores'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resolve the deep-linked itemId (from StockList's "View in Fabric Lot Register" link) into a
    // full Item so the autocomplete shows its name instead of sitting empty while the id filter is
    // already active. Same lookup-by-id-over-a-page pattern as PendingApprovalRequisitionDetails
    // and MaterialConsumptionEditor — ItemService has no get-by-id endpoint.
    useEffect(() => {
        const itemId = searchParams.get('itemId');
        if (!itemId) return;
        ItemService.getItemsByInstitution(0, 500)
            .then(res => {
                const match = (res.content || []).find(i => i.id === itemId);
                if (match) setSelectedItem(match);
            })
            .catch(err => toast.showApiError(err, 'Failed to load item'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (filters: StockLotFilters, pageToLoad: number) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setLoading(true);
        try {
            const res = await StockService.searchLots(institutionId, filters, pageToLoad, PAGE_SIZE);
            setRows(res.content);
            setPage(res.number);
            setTotalElements(res.totalElements);
        } catch (err) {
            toast.showApiError(err, 'Failed to load fabric lots');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    useEffect(() => {
        load(appliedFilters, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    const handleApply = () => {
        setAppliedFilters({
            storeId: formStoreId || undefined,
            itemId: formItemId || undefined,
            shade: formShade.trim() || undefined,
            dyeLot: formDyeLot.trim() || undefined,
            grade: formGrade || undefined,
            onlyAvailable: formOnlyAvailable || undefined,
            search: formSearch.trim() || undefined,
        });
    };

    const handleReset = () => {
        setFormStoreId('');
        setFormItemId('');
        setSelectedItem(null);
        setFormShade('');
        setFormDyeLot('');
        setFormGrade('');
        setFormOnlyAvailable(false);
        setFormSearch('');
        setAppliedFilters(emptyFilters);
    };

    const handlePageChange = (_event: unknown, newPage: number) => {
        load(appliedFilters, newPage);
    };

    const handleDownload = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setDownloading(true);
        try {
            const blob = await StockService.downloadLotRegisterXlsx(institutionId, appliedFilters);
            triggerDownload(blob, `fabric-lot-register-${toLocalIsoDate()}.xlsx`);
        } catch (err) {
            toast.showApiError(err, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="store.lots.page">
            <Typography variant="h5" sx={{ mb: 2 }}>
                Lot Register
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.lots.filter">
                <SearchableSelect<Store>
                    options={stores}
                    value={formStoreId}
                    onChange={id => setFormStoreId(id)}
                    getOptionLabel={store => store.storeName}
                    data-help="store.lots.filter.store" label="Store"
                    placeholder="All stores"
                    size="small"
                    sx={{ flex: '1 1 220px', maxWidth: 280 }}
                />

                <Box sx={{ flex: '1 1 220px', maxWidth: 280 }} data-help="store.lots.filter.item">
                    <ItemAutocomplete
                        value={selectedItem}
                        onChange={item => {
                            setSelectedItem(item);
                            setFormItemId(item?.id ?? '');
                        }}
                        label="Item"
                        size="small"
                    />
                </Box>

                <TextField
                    size="small" data-help="store.lots.filter.shade" label="Shade" value={formShade}
                    onChange={e => setFormShade(e.target.value)}
                    sx={{ flex: '1 1 220px', maxWidth: 280 }}
                />

                <TextField
                    size="small" data-help="store.lots.filter.dyeLot" label="Dye Lot" value={formDyeLot}
                    onChange={e => setFormDyeLot(e.target.value)}
                    sx={{ flex: '1 1 220px', maxWidth: 280 }}
                />

                <TextField
                    select size="small" data-help="store.lots.filter.grade" label="Grade" value={formGrade}
                    onChange={e => setFormGrade(e.target.value as FabricGrade | '')}
                    sx={{ flex: '1 1 220px', maxWidth: 280 }}
                >
                    <MenuItem value="">All grades</MenuItem>
                    {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>

                <TextField
                    size="small" data-help="store.lots.filter.search" label="Search" value={formSearch}
                    onChange={e => setFormSearch(e.target.value)}
                    sx={{ flex: '1 1 220px', maxWidth: 280 }}
                />

                <FormControlLabel
                    sx={{ flex: '1 1 180px', maxWidth: 220 }}
                    control={
                        <Switch
                            checked={formOnlyAvailable}
                            onChange={e => setFormOnlyAvailable(e.target.checked)}
                        />
                    }
                    data-help="store.lots.filter.onlyWithStock" label="Only with stock"
                />

                <Box sx={{ display: 'flex', gap: 1, flex: '1 1 220px' }}>
                    <Button data-help="store.lots.apply" variant="contained" size="small" onClick={handleApply} disabled={loading}>
                        Apply
                    </Button>
                    <Button data-help="store.lots.reset" variant="outlined" size="small" onClick={handleReset} disabled={loading}>
                        Reset
                    </Button>
                    <Button
                        data-help="store.lots.export"
                        variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                        onClick={handleDownload} disabled={downloading}
                    >
                        Export XLSX
                    </Button>
                    {canLabel && (
                        <Button
                            data-help="store.lots.printLabels"
                            variant="outlined" size="small" startIcon={<PrintIcon />}
                            disabled={labelIds.length === 0} onClick={() => setLabelOpen(true)}
                        >
                            Print labels{labelIds.length > 0 ? ` (${labelIds.length})` : ''}
                        </Button>
                    )}
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} data-testid="fabric-lot-register-table" data-help-section="store.lots.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                                    {canLabel && <TableCell padding="checkbox" />}{/* Q3 */}
                                    <TableCell data-help="store.lots.col.store">Store</TableCell>
                                    <TableCell data-help="store.lots.col.itemCode">Item Code</TableCell>
                                    <TableCell data-help="store.lots.col.item">Item</TableCell>
                                    <TableCell data-help="store.lots.col.lotNo">Lot No</TableCell>
                                    <TableCell data-help="store.lots.col.receiptDate">Receipt Date</TableCell>
                                    <TableCell data-help="store.lots.col.expiry">Expiry</TableCell>{/* F6a */}
                                    <TableCell data-help="store.lots.col.dyeLot">Dye Lot</TableCell>
                                    <TableCell data-help="store.lots.col.shade">Shade</TableCell>
                                    <TableCell data-help="store.lots.col.grade">Grade</TableCell>
                                    <TableCell data-help="store.lots.col.width" align="right">Width (cm)</TableCell>
                                    <TableCell data-help="store.lots.col.gsm" align="right">GSM</TableCell>
                                    <TableCell data-help="store.lots.col.rolls" align="right">Rolls</TableCell>
                                    <TableCell data-help="store.lots.col.supplierBatch">Supplier Batch</TableCell>
                                    <TableCell data-help="store.lots.col.cost" align="right">{averageCosting ? 'Receipt Cost' : 'Unit Cost'}</TableCell>
                                    <TableCell data-help="store.lots.col.received" align="right">Received Qty</TableCell>
                                    <TableCell data-help="store.lots.col.remaining" align="right">Remaining Qty</TableCell>
                                    <TableCell data-help="store.lots.col.source">Source</TableCell>
                                    <TableCell data-help="store.lots.col.sourceRef">Source Ref</TableCell>
                                    <TableCell data-help="store.lots.col.notes">Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={20} align="center">No lots found</TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map(lot => (
                                        <TableRow key={lot.id} hover>
                                            {canLabel && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox data-help="store.lots.action.select" size="small" checked={labelIds.includes(lot.id)}
                                                        inputProps={{ 'aria-label': `Select lot ${lot.lotNo}` }}
                                                        onChange={() => setLabelIds(prev => prev.includes(lot.id) ? prev.filter(x => x !== lot.id) : [...prev, lot.id])} />
                                                </TableCell>
                                            )}{/* Q3 */}
                                            <TableCell>{dash(lot.storeName)}</TableCell>
                                            <TableCell>{dash(lot.itemCode)}</TableCell>
                                            <TableCell>{dash(lot.itemName)}</TableCell>
                                            <TableCell>{lot.lotNo}</TableCell>
                                            <TableCell>{lot.receiptDate ? formatDate(lot.receiptDate) : '—'}</TableCell>
                                            <TableCell sx={lot.expired ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                                {lot.expiryDate ? `${formatDate(lot.expiryDate)}${lot.expired ? ' (expired)' : ''}` : '—'}
                                            </TableCell>{/* F6a */}
                                            <TableCell>{dash(lot.dyeLot)}</TableCell>
                                            <TableCell>{dash(lot.shade)}</TableCell>
                                            <TableCell>{dash(lot.grade)}</TableCell>
                                            <TableCell align="right">{dash(lot.widthCm)}</TableCell>
                                            <TableCell align="right">{dash(lot.gsm)}</TableCell>
                                            <TableCell align="right">{dash(lot.rollCount)}</TableCell>
                                            <TableCell>{dash(lot.supplierBatchRef)}</TableCell>
                                            <TableCell align="right">{lot.unitCost != null ? formatMoney(lot.unitCost) : '—'}</TableCell>
                                            <TableCell align="right">{dash(lot.receivedQty)}</TableCell>
                                            <TableCell align="right">{dash(lot.remainingQty)}</TableCell>
                                            <TableCell>{lot.sourceType ? humanizeEnum(lot.sourceType) : '—'}</TableCell>
                                            <TableCell>{dash(lot.sourceRef)}</TableCell>
                                            <TableCell>{dash(lot.notes)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={totalElements}
                            page={page}
                            onPageChange={handlePageChange}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} lot${count === 1 ? '' : 's'}`}
                        />
                    </TableContainer>
                </>
            )}
            {labelOpen && <StoreLabelDialog open kind="LOT" ids={labelIds} onClose={() => setLabelOpen(false)} />}{/* Q3 */}
        </Box>
    );
};

export default FabricLotRegister;
